export class Cache<T = unknown> {
  private _cache = new Map<string, T>();

  /**
   * Takes in a function as a parameter and returns a cached function which results will be shared across calls.
   *
   * @param key static cache key OR function that will compute it given arguments of cached function
   * @param fn function which result will be cached
   * @returns cached version of fn
   */
  cached =
    <F extends (...args: any[]) => any>(
      key: string | ((...args: Parameters<F>) => string),
      fn: F,
    ) =>
    (...args: Parameters<F>): ReturnType<F> => {
      const cacheKey = typeof key === "string" ? key : key(...args);
      const value = this._cache.get(cacheKey) || fn(...args);
      this._cache.set(cacheKey, value);
      return value;
    };

  get(key: string) {
    return this._cache.get(key);
  }

  set(key: string, value: T) {
    return this._cache.set(key, value);
  }

  clear(key: string) {
    return this._cache.delete(key);
  }

  clearByPrefix(prefix: string) {
    const keys = [...this._cache.keys()].filter((k) => k.startsWith(prefix));

    keys.forEach((k) => this._cache.delete(k));
  }
}
